import type {
  EditItemCycleRatePlanRequest,
  EntryCycleRatePlanApi,
} from '../../../services'

export type RentCycleMode = 'DAILY' | 'MONTHLY' | 'CUSTOM'

export type ExtraDayChargeMode = 'PER_DAY_RATE' | 'PER_DAY_FIXED' | 'BY_DAY_RANGE'

export type CyclePlanApi = EntryCycleRatePlanApi

function parseExtraDayChargeSlabs(
  slabs: CyclePlanApi['extraDayChargeSlabs']
): Array<{ id?: number; uptoDays: number; charge: number }> {
  if (Array.isArray(slabs)) return slabs
  if (typeof slabs !== 'string' || slabs.trim() === '') return []
  try {
    const parsed: unknown = JSON.parse(slabs)
    return Array.isArray(parsed)
      ? (parsed as Array<{ id?: number; uptoDays: number; charge: number }>)
      : []
  } catch {
    return []
  }
}

/** Round per-day rate to at most 4 decimal places (e.g. 20/30 → 0.6667). */
export function roundPerDayRate(value: number): number {
  return Math.round(value * 10000) / 10000
}

/**
 * UI display for per-day rate: round to at most 4 decimals, without trailing zeros
 * (0.6667 stays; 0.68 not 0.6800).
 */
export function formatPerDayRateDisplay(value: number): string {
  if (!Number.isFinite(value)) return ''
  return String(parseFloat(roundPerDayRate(value).toFixed(4)))
}

export function formatPerDayFromCycle(rate: number, cycleDays: number): string {
  if (!cycleDays || cycleDays <= 0 || !Number.isFinite(rate)) return ''
  const q = rate / cycleDays
  if (!Number.isFinite(q)) return ''
  return formatPerDayRateDisplay(q)
}

export function hydrateExtraDayFromPlan(plan: CyclePlanApi): {
  mode: ExtraDayChargeMode
  slabRows: { uptoDays: string; charge: string }[]
  perDaySideField: string
} {
  const slabs = parseExtraDayChargeSlabs(plan.extraDayChargeSlabs)
  const hasSlabRows = slabs != null && slabs.length > 0
  if (hasSlabRows) {
    return {
      mode: 'BY_DAY_RANGE',
      slabRows: slabs.map((s) => ({
        uptoDays: String(s.uptoDays),
        charge: String(s.charge),
      })),
      perDaySideField: '',
    }
  }
  if (plan.perDayFixed != null) {
    return {
      mode: 'PER_DAY_FIXED',
      slabRows: [{ uptoDays: '', charge: '' }],
      perDaySideField: String(plan.perDayFixed),
    }
  }
  if (plan.perDayRate != null) {
    return {
      mode: 'PER_DAY_RATE',
      slabRows: [{ uptoDays: '', charge: '' }],
      perDaySideField: formatPerDayRateDisplay(plan.perDayRate),
    }
  }
  return {
    mode: 'PER_DAY_RATE',
    slabRows: [{ uptoDays: '', charge: '' }],
    perDaySideField: formatPerDayFromCycle(plan.cycleCharge, plan.cycleDays),
  }
}

export function cycleRatePlanForRequestStub(
  plan: CyclePlanApi
): EditItemCycleRatePlanRequest {
  const slabs = parseExtraDayChargeSlabs(plan.extraDayChargeSlabs)
  const hasSlabRows = slabs != null && slabs.length > 0
  const base: Pick<
    EditItemCycleRatePlanRequest,
    'id' | 'cycleDays' | 'cycleCharge'
  > = {
    id: plan.id,
    cycleDays: plan.cycleDays,
    cycleCharge: plan.cycleCharge,
  }
  if (hasSlabRows) {
    return {
      ...base,
      extraDayChargeSlabs: slabs.map((s) => ({
        uptoDays: s.uptoDays,
        charge: s.charge,
      })),
    }
  }
  if (plan.perDayFixed != null) {
    return { ...base, perDayFixed: plan.perDayFixed }
  }
  if (plan.perDayRate != null) {
    return { ...base, perDayRate: plan.perDayRate }
  }
  const pr =
    plan.cycleDays > 0 ? plan.cycleCharge / plan.cycleDays : undefined
  return pr != null && Number.isFinite(pr)
    ? { ...base, perDayRate: roundPerDayRate(pr) }
    : { ...base }
}
